import React, { useRef, useState } from "react";
import styled from "styled-components";
import { useIntl } from "gatsby-plugin-intl";
import { useStaticQuery, graphql } from "gatsby";
import Slider from "react-slick";
import { GatsbyImage } from "gatsby-plugin-image";

import KCButton from "Components/KCButton";
import { ArrowLeft, ArrowRight } from "Components/common/kcArrow";

const StyledWrapper = styled.div`
  width: 100%;
  padding: 3rem 1rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  flex-direction: column;
  background-color: ${(props) => props.theme.color[props.backgroundColor]};
  ${(props) => props.theme.responsive.mobile} {
    padding: 3rem 1rem;
    flex-direction: row;
  }
`;

const StyledContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-right: 0rem;
  width: 100%;
  ${(props) => props.theme.responsive.mobile} {
    width: 50%;
    margin-right: 1rem;
  }
`;

const StyledAssetWrapper = styled.div`
  flex: 1;
  width: 100%;
  margin-left: 0rem;
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.theme.responsive.mobile} {
    width: 50%;
    margin-left: 1rem;
    margin-top: 0rem;
  }
`;

const StyledTitle = styled.div`
  font-size: 0.9rem;
  font-weight: bold;
  text-transform: uppercase;
  color: ${(props) => props.theme.color.grey};
`;

const StyledReviewCarouselWrapper = styled.div`
  position: relative;
`;

const StyledReviewCarousel = styled(Slider)`
  position: relative;
  font-size: 0;
  * {
    outline: none !important;
  }
`;

const StyledReviewWrapper = styled.div`
  display: inline-flex !important;
  flex-direction: column;
  margin-top: 3rem;
  min-height: 18rem;
  align-items: flex-start;
`;

const StyledRatingWrapper = styled.div`
  display: flex;
`;

const StyledStar = styled(GatsbyImage)`
  width: 0.7rem;
  height: 0.7rem;
  margin-right: 0.4rem;
`;

const StyledReview = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-top: 1rem;
  color: ${(props) => props.theme.dimGrey};
  flex: 1;
`;

const StyledReviewControl = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  width: 100%;
`;
const StyledReviewCustomer = styled.div`
  margin-top: 2rem;
  font-size: 0.8rem;
  color: ${(props) => props.theme.color.grey};
`;

const StyledReviewCTAWrapper = styled.div`
  margin-top: 1rem;
`;

const StyledReviewArrows = styled.div`
  position: absolute;
  bottom: 0.4rem;
  right: 0.4rem;
  & > *:not(:first-child) {
    margin-left: 1rem;
  }
`;

const StyledAsset = styled(GatsbyImage)`
  width: 100%;
  max-height: 20rem;
`;

const StyledLogoTitle = styled.div`
  margin-top: 2rem;
  font-size: 1rem;
  font-weight: bold;
  color: ${(props) => props.theme.color.grey};
`;

const StyledLogoWrapper = styled.div`
  display: flex;
  margin-top: 0.6rem;
`;

var scrollSettings = {
  arrows: false,
  touchMove: true,
  accessibility: false,
  infinite: true,
  autoplay: true,
  autoplaySpeed: 4000,
};

const StyledLogo = styled.img`
  margin: 0 0.4rem;
  width: 3rem;
  height: 2rem;
  object-fit: contain;
`;

export default function LongPress({
  asset,
  title,
  logos,
  reviews,
  backgroundColor,
}) {
  const intl = useIntl();
  const { star, specStyles } = useStaticQuery(graphql`
    {
      star: file(relativePath: { eq: "Icon/star.png" }) {
        childImageSharp {
          gatsbyImageData(width: 40, layout: CONSTRAINED)
        }
      }
    }
  `);

  const scrollWrapperRef = useRef(null);
  const [curIndex, setCurIndex] = useState(0);
  const handleNext = () => {
    scrollWrapperRef.current.slickNext();
  };

  const handlePrevious = () => {
    scrollWrapperRef.current.slickPrev();
  };

  const handleCarouselGoTo = (index) => {
    scrollWrapperRef.current.slickGoTo(index);
  };

  const getSpecStyle = (ID) => {
    let style = specStyles.nodes.find((style) => style.strapiId === ID);
    return `${style.design.name} in ${style.color.name}`;
  };

  return (
    <StyledWrapper backgroundColor={backgroundColor}>
      <StyledContentWrapper>
        <StyledTitle>{title[intl.locale]}</StyledTitle>
        <StyledReviewCarouselWrapper>
          <StyledReviewCarousel
            ref={scrollWrapperRef}
            beforeChange={(oldI, newI) => {
              setCurIndex(newI);
            }}
            {...scrollSettings}
          >
            {reviews.map((review, i) => (
              <StyledReviewWrapper key={i}>
                <StyledRatingWrapper>
                  {[...Array(review.stars)].map((x, i) => (
                    <StyledStar
                      key={i}
                      fluid={star.childImageSharp.gatsbyImageData}
                    />
                  ))}
                </StyledRatingWrapper>
                <StyledReview>"{review.caption}"</StyledReview>
                <StyledReviewControl>
                  <div>
                    <StyledReviewCustomer>
                      -{review.customer} wearing {getSpecStyle(review.style)}
                    </StyledReviewCustomer>

                    <StyledReviewCTAWrapper>
                      <KCButton
                        size="small"
                        label={`SHOP THE ${getSpecStyle(review.style)}`}
                      />
                    </StyledReviewCTAWrapper>
                  </div>
                </StyledReviewControl>
              </StyledReviewWrapper>
            ))}
          </StyledReviewCarousel>
          <StyledReviewArrows>
            <ArrowLeft onClick={() => handlePrevious()} />
            <ArrowRight onClick={() => handleNext()} />
          </StyledReviewArrows>
        </StyledReviewCarouselWrapper>
      </StyledContentWrapper>
      <StyledAssetWrapper>
        <StyledAsset fluid={asset.childImageSharp.gatsbyImageData} />
        <StyledLogoTitle>Also Seen In</StyledLogoTitle>
        <StyledLogoWrapper>
          {logos.map((logo, i) => (
            <StyledLogo key={i} src={logo.url} />
          ))}
        </StyledLogoWrapper>
      </StyledAssetWrapper>
    </StyledWrapper>
  );
}

export const query = graphql`
  fragment longPressModal on STRAPI_ComponentPressLongPress {
    id
    asset {
      imageFile {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
    title {
      en
      es
      it
    }
    logos {
      url
    }
    backgroundColor {
      color
    }
    reviews {
      caption
      customer
      style {
        kcID
        lens {
          name
        }
        design {
          name
        }
      }
      stars
    }
  }
`;
